import { Box } from "@mui/material";
import ShareDirectLinkModal from "app/components/AllAvailability/ShareDirectLinkModal";
import { getOrInitTwilioClient } from "app/DesktopLayout/twilioClient";
import { baseWebUrl } from "lib/constants";
import { useCreateAvailabilityMutation } from "lib/features/availability/availabilityService";
import {
  Availability,
  AvailabilityDTO,
  AvailabilitySetup,
  DirectLinkAvailabilitySetup,
  RequestToBookSetup,
  RequestToBookTime,
  Time,
} from "lib/features/availability/types";
import { useGetBusinessesByUserIdQuery } from "lib/features/business/businessService";
import { Item } from "lib/features/item/types";
import { selectCustomerDetails, selectIsExistingBookingLink } from "lib/features/message/messageSlice";
import {
  CreateReferralDto,
  useCreateReferralMutation,
} from "lib/features/referral/referralService";
import { User } from "lib/features/user/types";
import { selectUser } from "lib/features/user/userSlice";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import chatServices from "services/chat-service";
import { addAvailabilityStyles } from "styles/AvailabilityDirectory/addAvailability.styles";
import { updateEndTime } from "util/commonHelper";
import { getDatesBetween, getDayName, getTimeBetween, replaceDatePart } from "util/dateFunctions";
import AvailabilityFormModal from "../components/AvailabilityFormModal";
import AvailabilityHeader from "../components/AvailabilityHeader";
import AvailabilityStepForm from "../steps/availability-step/AvailabilityStepForm";
import ReviewAndSaveForm from "../steps/review-and-save-step/ReviewAndSaveForm";
import { BookingType } from "./itemBookingType";

interface AvailabilityFormInterface {
  itemDetails: Item;
  availability?: Availability;
}

const AvailabilityForm = ({
  itemDetails,
  availability,
}: AvailabilityFormInterface) => {
  const customerDetails = useSelector(selectCustomerDetails);
  const isExistingBookingLink = useSelector(selectIsExistingBookingLink);

  const [step, setStep] = useState(1);
  const [bookingType, setBookingType] = useState<BookingType | null>(null);
  const [availabilityModalOpen, setAvailabilityModalOpen] =
    useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [isShareDirectLinkModal, setIsShareDirectLinkModal] =
    useState<boolean>(false);
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [createAvailability, { isLoading }] = useCreateAvailabilityMutation();

  // Fetch business data to get business avatar
  const { data: businessData } = useGetBusinessesByUserIdQuery(
    { id: user?.id ?? null },
    { skip: !user?.id }
  );

  const [createReferral] = useCreateReferralMutation();

  const [availabilitySetupInstantBooking, setAvailabilitySetupInstantBooking] =
    useState<AvailabilitySetup | null>(null);

  const [availabilitySetupRequestToBook, setAvailabilitySetupRequestToBook] =
    useState<RequestToBookSetup | null>(null);

  const [availabilitySetupDirectBooking, setAvailabilitySetupDirectBooking] =
    useState<DirectLinkAvailabilitySetup | null>(null);

  const [selectedInfluencer, setSelectedInfluencer] = useState<User>();

  //Initial setup
  const initialDate = new Date();
  initialDate.setHours(12, 0, 0, 0);

  const placeholderTimeInstantBooking: Time = {
    start_time: availability ? new Date(availability.start_time) : initialDate,
    end_time: availability ? availability.end_time : initialDate,
    duration: availability
      ? getTimeBetween(availability.start_time, availability.end_time)
      : 0,
    quantity: Number(itemDetails?.itemDetails?.[0]?.capacity),
  };

  const placeholderTimeRequestToBook: RequestToBookTime = {
    start_time: availability ? new Date(availability.start_time) : initialDate,
    end_time: availability ? availability.end_time : initialDate,
    duration: availability
      ? getTimeBetween(availability.start_time, availability.end_time)
      : 0,
  };

  // Separate initialization for each booking type
  const initializeAvailabilitySetup = (type: BookingType) => {
    if (type === BookingType.INSTANT_BOOKING) {
      setAvailabilitySetupInstantBooking({
        item_id: itemDetails?.id,
        start_date: initialDate,
        end_date: initialDate,
        private:
          customerDetails?.customer_name && isExistingBookingLink
            ? true
            : false,
        days: [getDayName(initialDate.getDay())],
        times: [placeholderTimeInstantBooking],
      });
    } else if (type === BookingType.REQUEST_TO_BOOK) {
      setAvailabilitySetupRequestToBook({
        item_id: itemDetails?.id,
        start_date: availability ? availability.start_time : initialDate,
        end_date: availability ? availability.end_time : initialDate,
        is_range: false,
        days_of_week: [getDayName(initialDate.getDay())],
        requestToBookTimes: [placeholderTimeRequestToBook],
      });
    }
  };

  const onSubmit = (values: any) => {
    if (bookingType === BookingType.INSTANT_BOOKING) {
      handleInstantBookingSubmit(values);
    } else if (bookingType === BookingType.REQUEST_TO_BOOK) {
      handleRequestToBookSubmit(values);
    }
  };

  const handleInstantBookingSubmit = (values: any) => {
    if (step === 1) {
      setStep(2);
      setAvailabilitySetupInstantBooking(values);
    } else if (step === 2 && isExistingBookingLink) {
      // processDirectBookingLink(values);
      setAvailabilitySetupDirectBooking({
        item_id: itemDetails?.id,
        start_date: initialDate,
        private: true,
        times: placeholderTimeInstantBooking,
      });
      setIsShareDirectLinkModal(true);
    } else if (step === 2 && !isExistingBookingLink) {
      console.log("Instant Booking Save Values: ", values);
      processInstantBookingSave(values);
    }
  };

  const handleRequestToBookSubmit = (values: any) => {
    if (step === 1) {
      setStep(2);
      setAvailabilitySetupRequestToBook(values);
    } else if (step === 2) {
      console.log("final values: ", values);
    }
  };

  // Separate save logic for Instant Booking
  const processCreateAndShareBookingLink = async () => {
    if (!user?.id || !availabilitySetupDirectBooking || !selectedInfluencer?.id) return;

    setLoader(true);
    const availabilityDTO: AvailabilityDTO = {
      item_id: availabilitySetupDirectBooking?.item_id,
      start_time: replaceDatePart(
        availabilitySetupDirectBooking?.times.start_time,
        availabilitySetupDirectBooking?.start_date
      ),
      end_time: updateEndTime(
        availabilitySetupDirectBooking?.times.start_time,
        itemDetails?.itemDetails?.[0]?.duration
      ),
      private: availabilitySetupDirectBooking?.private,
      quantity: availabilitySetupDirectBooking?.times?.quantity,
    };
    const availabilitiesToAdd = [availabilityDTO];

    const avaialability = await createAvailability(availabilitiesToAdd).unwrap();

    //  step2: create the referral
    const newReferral: CreateReferralDto = {
      user_id: user?.id,
      business_id: itemDetails?.business?.id,
      availability_id: avaialability?.data?.[0]?.id,
      item_id: itemDetails?.id,
      referral_type: 1,
      state: 1,
    };

    const referral = await createReferral(newReferral).unwrap();

    const url = baseWebUrl + "/checkout/" + referral?.id;
    const message = `${url}`;

    // step3: create or reuse a Twilio conversation and send the message
    const payload = {
      participants: [String(user.id), String(selectedInfluencer.id)],
      initialMessage: {
        authorId: String(user.id),
        content: message,
      },
    };

    const response = await chatServices.createChannel(payload);
    const sid = response?.data?.conversation as string;

    setLoader(false);
    if (sid) {
      navigate(`/chat/${sid}`);
    }
  };

  // Separate save logic for Instant Booking
  const processInstantBookingSave = async (values: AvailabilitySetup) => {
    if (!user?.id) return;
    const dates = getDatesBetween(values.start_date, values.end_date).filter(
      (date) => values.days.includes(getDayName(date.getDay()))
    );
    const availabilitiesToAdd: AvailabilityDTO[] = [];
    dates.forEach((date) => {
      values.times.forEach((time) => {
        const availability: AvailabilityDTO = {
          item_id: values.item_id,
          start_time: replaceDatePart(time.start_time, date),
          end_time: updateEndTime(
            time.start_time,
            itemDetails?.itemDetails?.[0]?.duration
          ),
          private: values.private,
          quantity: Number(time.quantity),
        };
        console.log("Availability to add:", availability);

        availabilitiesToAdd.push(availability);
      });
    });

    if (values.private && values?.referee_id) {
      setLoader(true);
      // step1: create the availability
      const avaialability = await createAvailability(availabilitiesToAdd).unwrap();
      //  step2: create the referral
      const newReferral: CreateReferralDto = {
        user_id: user.id,
        business_id: itemDetails.business.id,
        availability_id: avaialability?.[0]?.id,
        item_id: itemDetails.id,
        customer_name: selectedInfluencer?.business_name ? selectedInfluencer?.business_name : selectedInfluencer?.first_name + " " + selectedInfluencer?.last_name,
        referral_type: 1,
        state: 1,
      };

      const referral = await createReferral(newReferral).unwrap();

      const url = baseWebUrl + "/checkout/" + referral?.id;
      const message = `${url}`;

      // step3: create or reuse a Twilio conversation and send the message
      const twilioPayload = {
        participants: [String(user.id), String(values.referee_id)],
        initialMessage: {
          authorId: String(user.id),
          content: message,
        },
      };

      const twilioResponse = await chatServices.createChannel(twilioPayload);
      const sid = twilioResponse?.data?.conversation as string;

      const fetchToken = async () => {
        const res = await chatServices.getUserTokenForChat(String(user.id));
        return String(res.data.token);
      };

      const client = await getOrInitTwilioClient(fetchToken);
      const conversation = await client.getConversationBySid(sid);

      // Send the message directly via Twilio SDK
      await conversation.sendMessage(url);

      setLoader(false);

      if (sid) {
        const userAvatarForChat = businessData?.[0]?.businessImages?.[0]?.uri || user?.avatar || '_';
        navigate(`/chat/${sid}/${encodeURIComponent(selectedInfluencer?.businessImage || selectedInfluencer?.avatar || '_')}/${encodeURIComponent(selectedInfluencer?.business_name ? selectedInfluencer?.business_name : selectedInfluencer?.first_name + " " + selectedInfluencer?.last_name || '_')}/${encodeURIComponent(userAvatarForChat)}`);
      } else {
        navigate("/my-items");
      }
    } else {
      await createAvailability(availabilitiesToAdd).unwrap();
      setAvailabilityModalOpen(true);
    }
  };

  useEffect(() => {
    if (
      itemDetails?.item_type === 1 ||
      itemDetails?.item_type === 2 ||
      itemDetails?.item_type === 4
    ) {
      setBookingType(BookingType.INSTANT_BOOKING);
      initializeAvailabilitySetup(BookingType.INSTANT_BOOKING);
    } else if (itemDetails?.item_type === 3) {
      setBookingType(BookingType.REQUEST_TO_BOOK);
      initializeAvailabilitySetup(BookingType.REQUEST_TO_BOOK);
    }
  }, [itemDetails]);

  return (
    <Box width="100%" position="relative">
      {/* Header */}
      <AvailabilityHeader
        step={step}
        setStep={setStep}
        bookingType={bookingType}
        availabilitySetup={availabilitySetupInstantBooking}
      />

      {/* Form and Steps */}
      <Box sx={addAvailabilityStyles.formBox}>
        {/* INSTANT_BOOKING Step 1 */}
        {step === 1 &&
          bookingType === BookingType.INSTANT_BOOKING &&
          availabilitySetupInstantBooking && (
            <>
              <AvailabilityStepForm
                itemDetails={itemDetails}
                availabilitySetup={availabilitySetupInstantBooking}
                selectedInfluencer={selectedInfluencer}
                setSelectedInfluencer={setSelectedInfluencer}
                isEditing={false}
                handleOpenRefereeModal={function (): void { }}
                submit={function (values: any): void {
                  onSubmit(values);
                }}
              />
            </>
          )}

        {step === 2 && bookingType === BookingType.INSTANT_BOOKING && (
          <>
            <ReviewAndSaveForm
              itemDetails={itemDetails}
              availabilitySetup={availabilitySetupInstantBooking}
              selectedInfluencer={selectedInfluencer}
              setSelectedInfluencer={setSelectedInfluencer}
              isEditing={false}
              setStep={setStep}
              isLoading={loader}
              submit={function (values: any): void {
                onSubmit(values);
              }}
            />
          </>
        )}
      </Box>

      <AvailabilityFormModal
        open={availabilityModalOpen}
        close={() => setAvailabilityModalOpen(false)}
        businessId={itemDetails?.business?.id}
      />

      <ShareDirectLinkModal
        item={itemDetails}
        createAndShareAvailabilityData={availabilitySetupDirectBooking}
        open={isShareDirectLinkModal}
        close={() => {
          setIsShareDirectLinkModal(false);
        }}
        loading={loader}
        processCreateAndShareBookingLink={processCreateAndShareBookingLink}
      />
    </Box>
  );
};

export default AvailabilityForm;
